// src/hooks/useUploadToasts.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * useUploadToasts
 * --------------
 * Manages a global, cross-route upload toast model (e.g., bottom-right status).
 *
 * Responsibilities:
 *  - Track multiple concurrent uploads by uploadId.
 *  - Provide callbacks (onUploadStatus, onUploadProgress) that recording pods/pages can call.
 *  - Derive a single toast view model that summarises the "primary" active upload.
 *  - Auto-cleanup completed/error uploads after a short delay.
 *  - Watchdog: if progress reaches ~100% but no terminal status arrives (e.g. missed callback
 *    or post-upload finalisation taking too long), automatically transitions the upload to a
 *    terminal state so the toast does not hang indefinitely.
 *
 * Upload statuses expected:
 *  - "uploading" | "paused" | "success" | "error"
 *
 * @returns {{
 *   toastModel: null | { isOpen: true, percent: number, status: string, count: number },
 *   onUploadStatus: ({ uploadId: string, status: string, variant?: string }) => void,
 *   onUploadProgress: ({ uploadId: string, progressPct: number }) => void
 * }}
 */
export default function useUploadToasts() {
  // uploads[uploadId] = { progressPct, status, variant, startedAt }
  const [uploads, setUploads] = useState({});

  // Cleanup timers remove completed/error uploads from state
  const cleanupTimersRef = useRef({});

  // Watchdog timers prevent "stuck uploading" when progress is ~100% but no terminal status arrives
  const completionWatchdogsRef = useRef({});

  const clearTimer = (mapRef, uploadId) => {
    const t = mapRef.current[uploadId];
    if (t) window.clearTimeout(t);
    delete mapRef.current[uploadId];
  };

  const scheduleRemoval = useCallback((uploadId, ms) => {
    clearTimer(cleanupTimersRef, uploadId);

    cleanupTimersRef.current[uploadId] = window.setTimeout(() => {
      setUploads((prev) => {
        const next = { ...prev };
        delete next[uploadId];
        return next;
      });
      clearTimer(cleanupTimersRef, uploadId);
      clearTimer(completionWatchdogsRef, uploadId);
    }, ms);
  }, []);

  /**
   * If we are at/near 100% but still "uploading", start a watchdog timer that
   * auto-finalises the upload after a short grace period.
   *
   * This protects the UI from hanging if the success/error callback is missed or
   * if a post-upload step (e.g. Firestore finalisation) never reports terminal state.
   */
  const armCompletionWatchdog = useCallback(
    (uploadId, graceMs = 10_000) => {
      if (!uploadId) return;

      // Only arm one watchdog at a time per uploadId
      if (completionWatchdogsRef.current[uploadId]) return;

      completionWatchdogsRef.current[uploadId] = window.setTimeout(() => {
        setUploads((prev) => {
          const cur = prev[uploadId];
          if (!cur) return prev;

          // If already terminal, do nothing
          if (cur.status === "success" || cur.status === "error") return prev;

          // Auto-mark as success to avoid indefinite "uploading" toast
          const next = { ...prev };
          next[uploadId] = { ...cur, status: "success", progressPct: 100 };
          return next;
        });

        // Remove shortly after showing success
        scheduleRemoval(uploadId, 2500);

        clearTimer(completionWatchdogsRef, uploadId);
      }, graceMs);
    },
    [scheduleRemoval],
  );

  const onUploadStatus = useCallback(
    ({ uploadId, status, variant }) => {
      if (!uploadId) return;

      setUploads((prev) => {
        const next = { ...prev };
        next[uploadId] = {
          ...(next[uploadId] || {
            progressPct: 0,
            startedAt: Date.now(),
            variant: variant || "",
          }),
          status,
          variant: variant ?? next[uploadId]?.variant ?? "",
        };
        return next;
      });

      // Terminal states: remove shortly after showing the message
      if (status === "success") {
        clearTimer(completionWatchdogsRef, uploadId);
        scheduleRemoval(uploadId, 2500);
        return;
      }
      if (status === "error") {
        clearTimer(completionWatchdogsRef, uploadId);
        scheduleRemoval(uploadId, 6000);
        return;
      }

      // Non-terminal: cancel any pending removal
      if (status === "uploading" || status === "paused") {
        clearTimer(cleanupTimersRef, uploadId);
        // leave watchdog behaviour to progress events (below)
      }
    },
    [scheduleRemoval],
  );

  const onUploadProgress = useCallback(
    ({ uploadId, progressPct }) => {
      if (!uploadId) return;

      const n = Number(progressPct);
      if (!Number.isFinite(n)) return;

      setUploads((prev) => {
        const next = { ...prev };

        if (!next[uploadId]) {
          next[uploadId] = {
            status: "uploading",
            variant: "",
            startedAt: Date.now(),
            progressPct: 0,
          };
        }

        const cur = next[uploadId];
        next[uploadId] = { ...cur, progressPct: n };

        return next;
      });

      // If we are basically done but never receive a terminal status, prevent UI hang.
      if (n >= 99.9) {
        armCompletionWatchdog(uploadId, 10_000);
      }
    },
    [armCompletionWatchdog],
  );

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(cleanupTimersRef.current).forEach((t) =>
        window.clearTimeout(t),
      );
      Object.values(completionWatchdogsRef.current).forEach((t) =>
        window.clearTimeout(t),
      );
      cleanupTimersRef.current = {};
      completionWatchdogsRef.current = {};
    };
  }, []);

  /**
   * Derive the single toast view model:
   * - Prefer most recent active upload (uploading/paused).
   * - If nothing active, hide toast.
   */
  const toastModel = useMemo(() => {
    const items = Object.entries(uploads).map(([uploadId, u]) => ({
      uploadId,
      ...u,
    }));
    if (items.length === 0) return null;

    const active = items.filter(
      (u) => u.status === "uploading" || u.status === "paused",
    );
    if (active.length === 0) return null;

    active.sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));
    const primary = active[0];

    return {
      isOpen: true,
      percent: Number.isFinite(primary.progressPct) ? primary.progressPct : 0,
      status: primary.status || "uploading",
      count: active.length,
    };
  }, [uploads]);

  return { toastModel, onUploadStatus, onUploadProgress };
}
